import { memo, useCallback, useMemo, useState } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import { type Timeseries } from "../../types.ts";
import { computeXmr, type AnomalyRange, type AnomalyType, type Xmr } from "../../metrics/xmr.ts";
import { type KalmanResult } from "../../metrics/kalman.ts";

export type AxisFormatter = (value: number) => string;

export type KalmanViewMode = "standard" | "kalman";

const kalmanInfoHtml = [
  "<strong>Kalman Filter</strong><br/>",
  "Estimates the underlying true value by combining each new observation ",
  "with a prediction from the previous state. Noisier days are weighted ",
  "less, producing a smoother trend line.<br/><br/>",
  "<strong>Hyperparameters</strong><br/>",
  "<em>Q (process noise)</em> — set to 10% of the XMR σ², ",
  "so the filter adapts to each budget's volatility.<br/>",
  "<em>R (measurement noise)</em> — set to the XMR σ², which estimates ",
  "short-term noise from the moving range between consecutive points.<br/><br/>",
  "Both values are derived from the data — no manual tuning required. ",
  "The filter operates in log space, matching the XMR transform.",
].join("");

/** Standalone toggle for use in ChartFrame headerExtra or similar. */
export function KalmanToggle({
  activeMode,
  onModeChange,
}: {
  activeMode: KalmanViewMode;
  onModeChange: (mode: KalmanViewMode) => void;
}) {
  const [infoVisible, setInfoVisible] = useState(false);
  return (
    <div className="xmr-kalman-toggle no-print" onMouseLeave={() => setInfoVisible(false)}>
      <div className="grain-toggle">
        <button
          className={`grain-toggle__btn${activeMode === "standard" ? " grain-toggle__btn--active" : ""}`}
          onClick={() => onModeChange("standard")}
        >
          Standard
        </button>
        <button
          className={`grain-toggle__btn${activeMode === "kalman" ? " grain-toggle__btn--active" : ""}`}
          onClick={() => onModeChange("kalman")}
        >
          Kalman{" "}
          <span className="grain-toggle__info-icon" onMouseEnter={() => setInfoVisible(true)}>
            i
          </span>
        </button>
      </div>
      {infoVisible && (
        <div className="xmr-kalman-popover" role="tooltip" dangerouslySetInnerHTML={{ __html: kalmanInfoHtml }} />
      )}
    </div>
  );
}

interface XmrChartProps {
  xmr: Xmr;
  kalman?: KalmanResult;
  /** When provided, renders title + inline toggle (Budget Detail page). */
  title?: string;
  /** When provided, chart is controlled externally (toggle rendered by parent). */
  viewMode?: KalmanViewMode;
  axisFormatter?: AxisFormatter;
  axisName?: string;
}

function XmrChart({ xmr, kalman, title, viewMode: controlledMode, axisFormatter, axisName }: XmrChartProps) {
  const [internalMode, setInternalMode] = useState<KalmanViewMode>("standard");
  const activeMode = controlledMode ?? (kalman ? internalMode : "standard");

  const n = xmr.ts.timestamps.length;
  const dense = n > DATAZOOM_THRESHOLD;

  // Default zoom: last 28 days as a percentage of the full range
  const defaultZoomStart = dense ? Math.max(0, ((n - 28) / n) * 100) : 0;
  // Track zoom as percentages so we can feed them back into the option
  const [zoomPct, setZoomPct] = useState<{ start: number; end: number }>({ start: defaultZoomStart, end: 100 });

  // Reset zoom synchronously when data length changes (e.g. day/week switch).
  // Uses the "derive state from props during render" pattern instead of useEffect
  // to avoid a flash of the stale zoom range before the effect fires.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevN, setPrevN] = useState(n);
  if (prevN !== n) {
    setPrevN(n);
    setZoomPct({ start: defaultZoomStart, end: 100 });
  }

  // Convert zoom percentages to indices
  const zoomRange = useMemo((): [number, number] => {
    const si = Math.round((zoomPct.start / 100) * (n - 1));
    const ei = Math.round((zoomPct.end / 100) * (n - 1));
    return [Math.max(0, si), Math.min(n - 1, ei)];
  }, [zoomPct, n]);

  // Recompute XMR on the visible slice when zoomed
  const visibleXmr = useMemo(() => {
    if (!dense) return xmr;
    const [startIdx, endIdx] = zoomRange;
    if (startIdx === 0 && endIdx === n - 1) return xmr;
    const slicedTs: Timeseries = {
      timestamps: xmr.ts.timestamps.slice(startIdx, endIdx + 1),
      data: xmr.ts.data.slice(startIdx, endIdx + 1),
    };
    if (slicedTs.data.filter(v => v != null).length < 3) return xmr;
    return computeXmr(slicedTs);
  }, [xmr, dense, zoomRange, n]);

  const option = useMemo<EChartsOption>(
    () =>
      buildOption(
        xmr,
        visibleXmr,
        activeMode === "kalman" ? kalman : undefined,
        activeMode,
        zoomPct,
        axisFormatter,
        axisName,
      ),
    [xmr, visibleXmr, kalman, activeMode, zoomPct, axisFormatter, axisName],
  );

  const handleDataZoom = useCallback((params: unknown) => {
    const p = params as { batch?: Array<{ start?: number; end?: number }>; start?: number; end?: number };
    const start = p.batch?.[0]?.start ?? p.start;
    const end = p.batch?.[0]?.end ?? p.end;
    if (start == null || end == null) return;
    setZoomPct({ start, end });
  }, []);

  const onEvents = useMemo(() => (dense ? { datazoom: handleDataZoom } : undefined), [dense, handleDataZoom]);

  // Self-contained toggle — only rendered when title is provided and no external control
  const selfContainedToggle =
    kalman && title && controlledMode == null ? (
      <KalmanToggle activeMode={activeMode} onModeChange={setInternalMode} />
    ) : null;

  return (
    <div>
      {title && (
        <div className="xmr-chart-header">
          <h2 className="entity-detail__chart-title">{title}</h2>
          {selfContainedToggle}
        </div>
      )}
      <EChart option={option} height={400} onEvents={onEvents} />
    </div>
  );
}

const DATAZOOM_THRESHOLD = 35;

function buildOption(
  fullXmr: Xmr,
  visibleXmr: Xmr,
  kalman: KalmanResult | undefined,
  mode: KalmanViewMode,
  zoomPct: { start: number; end: number },
  axisFormatter?: AxisFormatter,
  axisName?: string,
): EChartsOption {
  // Full series for value/Kalman data; visible slice for control limits and anomalies
  const { ts } = fullXmr;
  const { avgX, upperNaturalProcessLimit, lowerNaturalProcessLimit, anomalies } = visibleXmr;
  const timestampMs = ts.timestamps.map(date => date.getTime());
  const visibleTimestampMs = visibleXmr.ts.timestamps.map(date => date.getTime());
  const n = timestampMs.length;
  const dense = n > DATAZOOM_THRESHOLD;
  // Symbol size adapts to visible point count (zooming in restores larger symbols)
  const visibleN = visibleTimestampMs.length;
  const symbolSize = visibleN > 60 ? 0 : visibleN > DATAZOOM_THRESHOLD ? 4 : 6;

  const valueSeries: SeriesOption =
    mode === "kalman"
      ? {
          name: "Observed",
          type: "scatter",
          data: timestampMs.map((ms, idx) => [ms, ts.data[idx]]),
          symbol: "circle",
          symbolSize: visibleN > 60 ? 3 : visibleN > DATAZOOM_THRESHOLD ? 4 : 5,
          itemStyle: { color: "#111", opacity: 0.4 },
          emphasis: { focus: "series" },
        }
      : {
          name: "Value",
          type: "line",
          data: timestampMs.map((ms, idx) => [ms, ts.data[idx]]),
          symbol: symbolSize > 0 ? "circle" : "none",
          symbolSize,
          lineStyle: { color: "#111" },
          itemStyle: { color: "#111" },
          emphasis: { focus: "series" },
          smooth: true,
        };

  const limitsStart = timestampMs[0];
  const limitsEnd = timestampMs[timestampMs.length - 1];

  const avgSeries: SeriesOption = {
    name: "Average",
    type: "line",
    data: [
      [limitsStart, avgX],
      [limitsEnd, avgX],
    ],
    showSymbol: false,
    lineStyle: { color: "#8c8b7c", type: "dashed" },
    emphasis: { disabled: true },
  };

  const unplSeries: SeriesOption = {
    name: "UNPL",
    type: "line",
    data: [
      [limitsStart, upperNaturalProcessLimit],
      [limitsEnd, upperNaturalProcessLimit],
    ],
    showSymbol: false,
    lineStyle: { color: "#bfbda7", type: "dashed" },
    emphasis: { disabled: true },
  };

  const lnplSeries: SeriesOption = {
    name: "LNPL",
    type: "line",
    data: [
      [limitsStart, lowerNaturalProcessLimit],
      [limitsEnd, lowerNaturalProcessLimit],
    ],
    showSymbol: false,
    lineStyle: { color: "#bfbda7", type: "dashed" },
    emphasis: { disabled: true },
  };

  const kalmanSeries: SeriesOption | null = kalman
    ? {
        name: "Kalman Estimate",
        type: "line",
        data: timestampMs
          .map((ms, idx) => (kalman.filtered[idx] != null ? [ms, kalman.filtered[idx]] : null))
          .filter(Boolean),
        showSymbol: false,
        lineStyle: { color: "#58655B", width: 2 },
        itemStyle: { color: "#58655B" },
        emphasis: { focus: "series" },
        smooth: true,
      }
    : null;

  const anomalySeries = buildAnomalySeries(visibleXmr.ts, anomalies, axisFormatter);

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      extraCssText: tooltipCss,
      valueFormatter: axisFormatter ? value => axisFormatter(Number(value)) : undefined,
    },
    xAxis: {
      type: "time",
      boundaryGap: false,
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: axisFormatter ?? defaultAxisLabel, margin: 12 },
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    grid: dense ? { ...defaultGrid, bottom: 70 } : defaultGrid,
    ...(dense
      ? {
          dataZoom: [
            {
              type: "slider",
              xAxisIndex: 0,
              filterMode: "none",
              start: zoomPct.start,
              end: zoomPct.end,
              height: 22,
              bottom: 10,
              borderColor: "transparent",
              backgroundColor: "rgba(29,29,31,0.04)",
              fillerColor: "rgba(29,29,31,0.08)",
              handleStyle: { color: "#8c8b7c", borderColor: "#8c8b7c" },
              dataBackground: { lineStyle: { color: "#bfbda7" }, areaStyle: { color: "rgba(191,189,167,0.15)" } },
              selectedDataBackground: {
                lineStyle: { color: "#8c8b7c" },
                areaStyle: { color: "rgba(140,139,124,0.15)" },
              },
              textStyle: { fontSize: 10, color: "#8c8b7c" },
            },
          ],
        }
      : {}),
    series: [valueSeries, ...(kalmanSeries ? [kalmanSeries] : []), avgSeries, unplSeries, lnplSeries, ...anomalySeries],
  } satisfies EChartsOption;
}

function buildAnomalySeries(ts: Timeseries, anomalies: AnomalyRange[], axisFormatter?: AxisFormatter): SeriesOption[] {
  const anomalyColor: Record<AnomalyType, string> = {
    outside_limits: "#ff0000",
    two_sigma: "#e17655",
    run_of_eight: "#927cff",
  };
  const anomalySymbol: Record<AnomalyType, string> = {
    outside_limits: "circle",
    two_sigma: "diamond",
    run_of_eight: "triangle",
  };
  const anomalyName: Record<AnomalyType, string> = {
    outside_limits: "Rule 1: Outside Limits",
    two_sigma: "Rule 2: 2 of 3 ≥2σ",
    run_of_eight: "Rule 3: Run of 8",
  };

  const buckets: Record<AnomalyType, Array<[number, number]>> = {
    outside_limits: [],
    two_sigma: [],
    run_of_eight: [],
  };

  const timeToIndex = new Map<number, number>();
  for (let i = 0; i < ts.timestamps.length; i++) {
    timeToIndex.set(ts.timestamps[i].getTime(), i);
  }

  for (const anomaly of anomalies) {
    const startIdx = timeToIndex.get(anomaly.start.getTime());
    const endIdx = timeToIndex.get(anomaly.end.getTime());
    if (startIdx === undefined || endIdx === undefined) continue;
    for (let idx = startIdx; idx <= endIdx; idx++) {
      buckets[anomaly.type].push([ts.timestamps[idx].getTime(), ts.data[idx]]);
    }
  }

  return (Object.keys(buckets) as AnomalyType[])
    .filter(type => buckets[type].length > 0)
    .map(type => ({
      name: anomalyName[type],
      type: "scatter",
      data: buckets[type],
      symbol: anomalySymbol[type],
      symbolSize: 12,
      itemStyle: {
        color: "#ffffff",
        borderColor: anomalyColor[type],
        borderWidth: 3,
      },
      emphasis: { focus: "series" },
      tooltip: {
        valueFormatter: (value: unknown) =>
          typeof value === "number" ? (axisFormatter ? axisFormatter(value) : value.toFixed(2)) : String(value),
      },
    }));
}

export default memo(XmrChart);
